<script setup>
import { useGenerateImageVariant } from "@core/composable/useGenerateImageVariant";
import { VNodeRenderer } from "@layouts/components/VNodeRenderer";
import { themeConfig } from "@themeConfig";
import authV2ForgotPasswordIllustrationDark from "@images/pages/auth-v2-forgot-password-illustration-dark.png";
import authV2ForgotPasswordIllustrationLight from "@images/pages/auth-v2-forgot-password-illustration-light.png";
import authV2MaskDark from "@images/pages/misc-mask-dark.png";
import authV2MaskLight from "@images/pages/misc-mask-light.png";
import { withQuery } from "ufo";

definePage({ meta: { layout: "blank" } });

const route = useRoute();
const router = useRouter();

const loading = ref();

const load = () => {
  loading.value = true;
  setTimeout(() => {
    loading.value = false;
  }, 3000);
};

const authThemeImg = useGenerateImageVariant(
  authV2ForgotPasswordIllustrationLight,
  authV2ForgotPasswordIllustrationDark
);
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark);

const refForm = ref();
const form = ref({
  loginId: null,
  email: null,
  mallId: null,
});

/*
 * Form Submit
 * */
const valid = ref("");
const errors = ref("");
const sendData = async (e) => {
  try {
    load();
    var res = await $api("/cms/auth/forgot-password", {
      method: "POST",
      body: e,
      onResponseError({ response }) {
        errors.value = response._data.message;

        setTimeout(() => (errors.value = false), 10000);
      },
    });

    if (res.statusCode === 200 || res.statusCode === 201) {
      valid.value = res.message;
      setTimeout(() => (valid.value = false), 10000);
      setTimeout(() => router.push({ path: "/login" }), 2000);
    }
  } catch (err) {
    errors.value = err._data.message;

    setTimeout(() => (errors.value = false), 10000);
  }
};

/*
 * Data Mall
 * */

const mallQueries = [];
const { data: mallQuery } = await useApiFront(withQuery("/cms/mall/all"));
mallQuery.value?.data.forEach((item) => {
  mallQueries.push({
    title: item.name,
    value: item.id,
  });
});
</script>

<template>
  <div class="auth-bg"></div>
  <VRow class="auth-wrapper bg-surface" no-gutters>
    <VCol
      class="d-flex align-center justify-center mx-auto"
      cols="12"
      lg="3"
      sm="4"
    >
      <VCard :max-width="500" border class="mt-12 mt-sm-0 pa-4">
        <VCardText>
          <VNodeRenderer :nodes="themeConfig.app.logo" class="mb-6 w-75" />
          <h4 class="text-h4 mb-1">Forgot Password?</h4>
          <p class="mb-0">
            Enter your email and we'll send you instructions to reset your
            password
          </p>
        </VCardText>

        <VCardText>
          <VAlert v-show="valid" type="success">
            {{ valid }}
          </VAlert>
          <VAlert v-show="errors" type="error">
            {{ errors }}
          </VAlert>
          <VForm
            @submit.prevent="
              () => {
                sendData(form);
              }
            "
          >
            <VRow>
              <!-- loginId -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.loginId"
                  :rules="[requiredValidator]"
                  autofocus
                  label="Login Id"
                  placeholder="Input Login Id"
                  type="text"
                />
              </VCol>

              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  :rules="[requiredValidator, emailValidator]"
                  autofocus
                  label="Email"
                  placeholder="Input email"
                  type="email"
                />
              </VCol>

              <!-- mallId -->
              <VCol cols="12">
                <AppSelect
                  v-model="form.mallId"
                  :items="mallQueries"
                  :rules="[requiredValidator]"
                  label="Mall *"
                  placeholder="Choose Mall"
                />
              </VCol>

              <!-- Reset link -->
              <VCol cols="12">
                <VBtn
                  :disabled="loading"
                  :loading="loading"
                  block
                  color="primary"
                  type="submit"
                >
                  Send Reset Link

                  <template #loader>
                    <span class="custom-loader">
                      <VIcon class="text-white" icon="tabler-refresh" />
                    </span>
                  </template>
                </VBtn>
              </VCol>

              <!-- back to login -->
              <VCol cols="12">
                <RouterLink
                  :to="{ name: 'login' }"
                  class="d-flex align-center justify-center"
                >
                  <VIcon class="flip-in-rtl" icon="tabler-chevron-left" />
                  <span>Back to login</span>
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
